import React from 'react';

import { Sheet } from '@iris/react-components/Sheet';

import { SubHeading } from 'reaxl';

import {
    createPriceAdvisorProps,
    extractPriceAdvisorData,
    getFairMarketRangeTitle,
    getPriceAdvisorSubHeader,
    usePriceAdvisorConfig,
} from '@/utilities/priceAdvisorUtils';

import PriceAdvisor from '@/components/PriceAdvisor';

/**
 * @summary Fair Market Range Sheet modal component that displays KBB pricing information
 * @description Renders a modal sheet containing KBB Fair Market Range or Private Party Range pricing tool.
 * Shows pricing comparison data in an iframe with contextual header text and KBB privacy link.
 * @param {Object} props - Component props
 * @param {boolean} props.isPrivateSeller - Whether this is for a private seller (true) or dealer (false)
 * @param {Object} props.listing - Vehicle listing data containing pricing, VIN, and type information
 * @param {Object} props.owner - Owner information containing location data for pricing calculations
 * @param {React.Element} props.trigger - The trigger element that opens the modal sheet
 * @param {string} [props.className=''] - Additional CSS classes to apply to the wrapper div
 * @returns {React.Component} Modal sheet component with KBB pricing information
 */
export default function FairMarketRangeSheet({ isPrivateSeller, listing, owner, trigger, className = '' }) {
    const config = usePriceAdvisorConfig();
    const priceAdvisorData = extractPriceAdvisorData(listing, owner);
    const priceAdvisorProps = createPriceAdvisorProps({
        isPrivateSeller,
        listing,
        owner,
        height: '308',
        config,
    });

    if (!trigger) {
        return null;
    }

    return (
        <div
            data-cmp="fairMarketRangeSheet"
            className={className}
        >
            <Sheet
                trigger={trigger}
                desktopPosition="center"
                title={(
                    <>
                        See how this price
                        <br />
                        compares
                    </>
                )}
                bodyClassName="iris:w-full iris:md:w-[376px]"
                modal
            >
                <div className="iris:mt-2 iris:text-left">
                    <SubHeading level={3}>
                        {getFairMarketRangeTitle(isPrivateSeller)}
                    </SubHeading>
                    <div className="iris:mt-3 iris:mb-0">
                        {getPriceAdvisorSubHeader(
                            priceAdvisorData.isNewCar,
                            priceAdvisorData.msrp,
                            priceAdvisorData.priceAdvisorRuleEnabled
                        )}
                    </div>
                    <PriceAdvisor {...priceAdvisorProps} />
                </div>
            </Sheet>
        </div>
    );
}
